/**
 * Event bus for application-wide events
 * Provides a simple pub/sub mechanism for components to communicate
 */

class EventBus {
  constructor() {
    this.subscribers = {};
  }

  /**
   * Subscribe to an event
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  on(event, callback) {
    if (!this.subscribers[event]) {
      this.subscribers[event] = [];
    }
    
    this.subscribers[event].push(callback);
    
    // Return unsubscribe function
    return () => {
      this.subscribers[event] = this.subscribers[event].filter(cb => cb !== callback);
      
      // Clean up empty subscriber arrays
      if (this.subscribers[event].length === 0) {
        delete this.subscribers[event];
      }
    };
  }

  /**
   * Emit an event with data
   * @param {string} event - Event name
   * @param {*} data - Event data
   */
  emit(event, data) {
    if (!this.subscribers[event]) {
      return;
    }
    
    // Call all subscribers with the data
    this.subscribers[event].forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in event handler for ${event}:`, error);
      }
    });
  }

  /**
   * Remove all subscribers for an event
   * @param {string} event - Event name
   */
  off(event) {
    if (event) {
      delete this.subscribers[event];
    } else {
      // If no event specified, clear all subscriptions
      this.subscribers = {};
    }
  }

  /**
   * Get number of subscribers for an event
   * @param {string} event - Event name
   * @returns {number} Number of subscribers
   */
  count(event) {
    return this.subscribers[event]?.length || 0;
  }

  /**
   * Check if an event has subscribers
   * @param {string} event - Event name
   * @returns {boolean} True if the event has subscribers
   */
  hasSubscribers(event) {
    return this.count(event) > 0;
  }

  /**
   * Get a list of all events with subscribers
   * @returns {string[]} Array of event names
   */
  getEvents() {
    return Object.keys(this.subscribers);
  }
}

// Create a singleton instance
const eventBus = new EventBus();

 

export default eventBus;